/**
 * Currency Utilities
 * 
 * Multi-currency support with formatting and conversion utilities.
 * Following dev guide: stateless helpers, deterministic operations.
 */

// Supported currencies
export interface Currency {
  code: string;
  name: string;
  symbol: string;
  symbolPosition: 'before' | 'after';
  decimalPlaces: number;
  thousandsSeparator: string;
  decimalSeparator: string;
}

// Currency definitions
export const CURRENCIES: Record<string, Currency> = {
  USD: {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  GEL: {
    code: 'GEL',
    name: 'Georgian Lari',
    symbol: '₾',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  RUB: {
    code: 'RUB',
    name: 'Russian Ruble',
    symbol: '₽',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ' ',
    decimalSeparator: ','
  }
};

// Get list of supported currency codes
export const SUPPORTED_CURRENCIES = Object.keys(CURRENCIES);

// Default currency
export const DEFAULT_CURRENCY = 'USD';

/**
 * Check if currency code is supported
 */
export function isSupportedCurrency(code: string): boolean {
  return SUPPORTED_CURRENCIES.includes(code.toUpperCase());
}

/**
 * Get currency definition
 */
export function getCurrency(code: string): Currency {
  const upperCode = code.toUpperCase();
  if (!CURRENCIES[upperCode]) {
    throw new Error(`Unsupported currency: ${code}`);
  }
  return CURRENCIES[upperCode];
}

/**
 * Format amount with currency symbol and proper formatting
 */
export function formatCurrencyAmount(amountInCents: number, currencyCode: string): string {
  const currency = getCurrency(currencyCode);
  const amount = amountInCents / 100;
  
  // Format the number according to currency rules
  const formattedNumber = formatNumber(amount, currency);
  
  // Position symbol correctly
  if (currency.symbolPosition === 'before') {
    return `${currency.symbol}${formattedNumber}`;
  } else {
    return `${formattedNumber} ${currency.symbol}`;
  }
}

/**
 * Format number according to currency rules
 */
function formatNumber(amount: number, currency: Currency): string {
  const fixedAmount = amount.toFixed(currency.decimalPlaces);
  const [integerPart, decimalPart] = fixedAmount.split('.');
  
  // Add thousands separator
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, currency.thousandsSeparator);
  
  if (currency.decimalPlaces === 0 || decimalPart === '00') {
    return formattedInteger;
  }
  
  return `${formattedInteger}${currency.decimalSeparator}${decimalPart}`;
}

/**
 * Parse currency input string to cents
 */
export function parseCurrencyInput(input: string, currencyCode: string): number {
  const currency = getCurrency(currencyCode);
  
  // Remove currency symbol and clean input
  let cleanInput = input
    .replace(currency.symbol, '')
    .replace(/\s/g, '') // Remove spaces
    .trim();
  
  // Replace currency decimal separator with dot for parsing
  if (currency.decimalSeparator !== '.') {
    cleanInput = cleanInput.replace(currency.decimalSeparator, '.');
  }
  
  // Remove thousands separators
  cleanInput = cleanInput.replace(new RegExp(`\\${currency.thousandsSeparator}`, 'g'), '');
  
  const amount = parseFloat(cleanInput);
  if (isNaN(amount)) {
    throw new Error('Invalid currency amount');
  }
  
  return Math.round(amount * 100);
}

/**
 * Get currency symbol for display
 */
export function getCurrencySymbol(currencyCode: string): string {
  return getCurrency(currencyCode).symbol;
}

/**
 * Get currency name for display
 */
export function getCurrencyName(currencyCode: string): string {
  return getCurrency(currencyCode).name;
}

/**
 * Convert cents to currency display value
 */
export function centsToDisplayValue(cents: number, currencyCode: string): string {
  const currency = getCurrency(currencyCode);
  const amount = cents / 100;
  return amount.toFixed(currency.decimalPlaces);
}

/**
 * Get currency input placeholder
 */
export function getCurrencyPlaceholder(currencyCode: string): string {
  const currency = getCurrency(currencyCode);
  const placeholder = '0' + currency.decimalSeparator + '00';
  
  if (currency.symbolPosition === 'before') {
    return `${currency.symbol}${placeholder}`;
  } else {
    return `${placeholder} ${currency.symbol}`;
  }
}

/**
 * Validate currency amount input
 */
export function validateCurrencyAmount(input: string, currencyCode: string): { valid: boolean; error?: string } {
  try {
    const currency = getCurrency(currencyCode);
    
    if (!input || input.trim() === '') {
      return { valid: false, error: 'Amount is required' };
    }
    
    const cents = parseCurrencyInput(input, currencyCode);
    
    if (cents <= 0) {
      return { valid: false, error: 'Amount must be greater than 0' };
    }
    
    if (cents > 100000000) { // $1M equivalent limit
      return { valid: false, error: 'Amount is too large' };
    }
    
    return { valid: true };
  } catch (error) {
    return { valid: false, error: 'Invalid amount format' };
  }
}

/**
 * Get list of currencies for dropdown
 */
export function getCurrencyOptions(): Array<{ value: string; label: string; symbol: string }> {
  return SUPPORTED_CURRENCIES.map(code => {
    const currency = CURRENCIES[code];
    return {
      value: code,
      label: `${currency.name} (${currency.code})`,
      symbol: currency.symbol
    };
  });
}
